import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Tooltip, Whisper } from 'rsuite';

import { getCategoryLabel } from '../../utils/expenseCategories';
import PayrollCopyableFigure from './PayrollCopyableFigure';
import PayrollCorrectionCell from './PayrollCorrectionCell';
import PayrollAdjustmentCell from './PayrollAdjustmentCell';
import PayrollCorrectionModal from './PayrollCorrectionModal';
import PayrollBulkReimbursementAdjustmentModal from './PayrollBulkReimbursementAdjustmentModal';
import PayrollReimbursementsModal from './PayrollReimbursementsModal';

// The pay-period summary, transposed: employees run along the top and the
// figures down the side. That way adding a person widens the sheet instead of
// lengthening it, and a row can be read straight across to compare everyone on
// the same measure - which is what this table is for.
//
// Part of the company-specific payroll module; see config/payrollFeature.js.

// A missing figure is an em dash, never a zero: zero is a fact about the pay
// run, the dash is a fact about the data. Confusing the two is how somebody
// gets paid nothing because a rate was never entered.
//
// Declared above the formatters because they return it too. There is one way to
// draw a missing figure on this sheet, and a cell that reached it through a
// formatter used to draw a different one - two hyphens beside an em dash in the
// next row, which looks like two kinds of missing rather than one.
const EMPTY = '—';

// The sync stamp, as a date and time somebody can match against an approval.
const fmtStamp = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
};

// How each reimbursement bucket is named on screen. The stage itself is decided
// in SQL - see loadReimbursements in routes/payroll.js.
// The expense part of a row's reimbursements - what the Reimbursements row
// shows. `reimbursements` also carries the reimbursement adjustment, which has
// its own row; a run filed before adjustments existed has no
// `reimbursementExpenses`, and its `reimbursements` was expenses only.
const expenseReimbursementOf = (employee) => Number(employee?.reimbursementExpenses ?? employee?.reimbursements) || 0;

const REIMBURSEMENT_STAGE_LABEL = {
  paid: 'paid',
  unpaid: 'unpaid',
  pending: 'awaiting approval',
};

const fmtHours = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return EMPTY;
  return n.toFixed(2);
};

const fmtPercent = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return EMPTY;
  return `${n.toFixed(1)}%`;
};

const fmtMoney = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return EMPTY;
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

// "5% of comp" / "$200 flat" - what was elected, under the dollars it resolved
// to. A percentage election on a period with no rate resolves to nothing, and
// this line is then the only thing that says why.
const describeElection = (contribution) => {
  if (!contribution) return '';
  if (contribution.mode === 'percent') return `${contribution.value}% of comp`;
  return `${fmtMoney(contribution.value)} flat`;
};

// Variance is the non-direct time TAWH could not absorb. Somebody with none -
// which is most people, and everybody whose extra hours were all billable -
// shows nothing rather than a zero competing for attention with the real ones.
const hasVariance = (employee) => Number(employee.varianceHours) > 0;

// Billable percentage against TAWH, banded the way the timesheet page bands it:
// the number is the point, the colour is only there to make an outlier findable
// in a wide row.
const billableTone = (percent) => {
  if (percent == null) return '';
  if (percent >= 75) return 'payroll-matrix__value--good';
  if (percent < 40) return 'payroll-matrix__value--low';
  return '';
};

// The calculation behind each row, shown on hover over the row label.
//
// Formulas, not explanations - the reader wants to know what produced the
// number, not why the rule exists.
//
// Keyed by the row label so the category, plan and journal rows, whose labels
// come from the server, line up without a second table to keep in step.
const ROW_HELP = {
  'TAWH': 'Weekdays in period x 8 x TAWH commitment %. Non-exempt are always 100%, so the line beneath shows their target hours per period instead.',
  'Classification': 'From the employee record.',
  'Rate': 'Latest pay rate effective on or before the period end date.',
  'Direct': 'Hours on TimeCamp tasks with billable = 1.',
  'Indirect': 'Hours on tasks with billable = 0, excluding the fringe tasks.',
  'Fringe Benefits': 'Hours on the time off, sick time and close date tasks.',
  'Total Hours': 'Direct + Indirect + Fringe Benefits.',
  'Payroll Variance': 'max(0, indirect hours - indirect costs) x rate.',
  'Billable %': 'Direct hours / TAWH.',
  'Paid Hours': 'Exempt: logged hours capped at TAWH, plus direct over TAWH, plus time off once direct passes TAWH, plus any correction. Non-exempt: total hours.',
  'PATO Used': 'Hours on the PATO task within this pay period.',
  'PATO Remaining': 'Company grant + policy changes + adjustments to the period end, less PATO hours taken year to date. Unlimited when the company policy is unlimited time off.',
  'Payroll Correction': 'Hours, entered by hand. Counts towards total hours, and is added to paid hours on top of the TAWH cap.',
  'Total Added Direct Hours': 'Direct hours worked beyond TAWH, or zero. The commitment is already covered by salary; these are the hours on top of it.',
  'Added Direct Hours Compensation': 'Total added direct hours x rate. Part of total compensation, shown on its own because it is the part that moves with what was logged.',
  'Total Compensation': 'Paid hours x rate.',
  'Reimbursements': 'What is owed for expenses: approved expenses paid in this period, plus approved expenses still unpaid whatever their date. Company card purchases are excluded. Expenses awaiting approval are shown beneath but are not part of the figure.',
  'Reimbursement Adjustment': 'Dollars, entered by hand: money owed that did not come through an expense, such as lunch for a meeting. Negative takes money off. Added to the reimbursements total and Cash Needed. The group button beside this label sets one amount and reason for several employees at once.',
  '401(k)': 'Election: % x total compensation, or a flat amount.',
  'Roth 401(k)': 'Election: % x total compensation, or a flat amount.',
  'Employer Match': 'min(match rate %, combined deferral / total compensation) x total compensation.',
  'Employee 401(k) total': 'Every employee pre-tax deferral in this run, added together.',
  'Employee Roth total': 'Every employee Roth deferral in this run, added together.',
  'Employer match total': 'Every employer match in this run, added together.',
  'Compensation total': 'Every employee total compensation in this run, added together.',
  'Reimbursements total': 'Every employee reimbursement and reimbursement adjustment in this run, added together. Excludes expenses still awaiting approval.',
  'Cash Needed': 'Compensation total + reimbursements total. What has to be paid out for this run.',
  // Backed by retirement.adjustment on the row - the label is what changed,
  // not the field. Renaming the stored figure would mean a migration and a
  // rewrite of every saved run snapshot, for a caption.
  'Loan Repayment': 'A cash amount entered by hand. Added to what is remitted; it does not change compensation, the deferrals or the match.',
  'Loan Repayment total': 'Every loan repayment in this run, added together.',
  'Total uploaded to Empower': 'Employee 401(k) + employee Roth + employer match + loan repayments, for the whole run.',
};

export default function PayrollMatrix({
  result,
  onEditSettings,
  onSaveCorrection,
  onSaveAdjustment,
  // The Reimbursement Adjustment row. Absent on a run that cannot take one, and
  // the row then shows plain figures - the same convention as onSaveCorrection.
  onSaveReimbursementAdjustment,
  // The row's group button: one amount and reason for several employees.
  onSaveReimbursementAdjustmentBulk,
  onExport,
  // Set only for a saved run, and only once live expenses have been found to
  // differ from the ones it was filed with. See PayrollReimbursementDriftModal.
  reimbursementDrift = null,
  onOpenReimbursementDrift,
  onSyncReimbursements,
  syncingReimbursements = false,
}) {
  // One dialog for the whole sheet. `target` is { employee, amount, kind } -
  // the amount travels with it because the cell has usually just been typed
  // into and that draft has not been stored yet. `kind` says which of the two
  // hand-entered rows it is: 'correction' (hours) or 'reimbursement' (dollars).
  // Declared above the empty-run guard below, since hooks have to run in the
  // same order on every render.
  const [correctionTarget, setCorrectionTarget] = useState(null);
  const [savingCorrection, setSavingCorrection] = useState(false);
  // Bumped whenever the dialog is dismissed without saving, to send the cells
  // back to their stored values.
  const [resetToken, setResetToken] = useState(0);

  // Whose reimbursement breakdown is open, if any.
  const [reimbursementsFor, setReimbursementsFor] = useState(null);

  // The Reimbursement Adjustment row's group dialog.
  const [bulkAdjustmentOpen, setBulkAdjustmentOpen] = useState(false);
  const [savingBulkAdjustment, setSavingBulkAdjustment] = useState(false);

  // The page toasts a failure and rethrows; rethrown again here so the dialog
  // stays open with what was entered.
  const applyBulkAdjustment = async (payload) => {
    setSavingBulkAdjustment(true);
    try {
      await onSaveReimbursementAdjustmentBulk(payload);
    } finally {
      setSavingBulkAdjustment(false);
    }
  };

  const employees = result?.employees || [];

  // The two hand-entered rows work identically; only where they save and which
  // note they keep differs.
  const HAND_ENTERED = {
    correction: { save: onSaveCorrection, noteKey: 'correctionNote' },
    reimbursement: { save: onSaveReimbursementAdjustment, noteKey: 'reimbursementAdjustmentNote' },
  };

  const requestReason = (employee, amount, kind = 'correction') => setCorrectionTarget({ employee, amount, kind });

  const dismissCorrection = () => {
    setCorrectionTarget(null);
    setResetToken((n) => n + 1);
  };

  // Clearing to zero skips the dialog: there is no adjustment left to explain,
  // so the note goes with it.
  const clearCorrection = async (employee, kind = 'correction') => {
    try {
      await HAND_ENTERED[kind].save({ employeeId: employee.employeeId, amount: 0, note: null });
    } catch {
      // The page toasts the failure; the cell re-reads the stored value.
    }
  };

  // Adjusting a correction that already carries a reason: keep the reason and
  // save straight through, rather than reopening the dialog on every nudge.
  const saveKeepingNote = async (employee, amount, kind = 'correction') => {
    const { save, noteKey } = HAND_ENTERED[kind];
    try {
      await save({ employeeId: employee.employeeId, amount, note: employee[noteKey] });
    } catch {
      // The page toasts the failure; the cell re-reads the stored value.
    }
  };

  const saveCorrection = async ({ kind = 'correction', ...payload }) => {
    setSavingCorrection(true);
    try {
      await HAND_ENTERED[kind].save(payload);
      setCorrectionTarget(null);
    } catch {
      // Left open with the draft intact so the entry is not lost to a failed
      // request - the page has already said what went wrong.
    } finally {
      setSavingCorrection(false);
    }
  };

  // Narrowest an employee column may get before the sheet scrolls instead of
  // squeezing. Only used to set the table's minimum width - the columns
  // themselves are left to share out whatever room there is.
  const MIN_EMPLOYEE_COL = 150;

  // The label column is measured from the text it actually renders, so it holds
  // the longest label without wrapping.
  //
  // Measured from the DOM rather than estimated from character counts: a
  // per-character guess was wrong enough to need two passes. The span is
  // nowrap, so its scrollWidth is the natural text width whatever the column is
  // currently set to - which is what makes measuring inside a fixed-layout
  // table work at all.
  const tableRef = useRef(null);
  const [labelColumnWidth, setLabelColumnWidth] = useState(200);

  // How wide the sheet is on screen, as opposed to how wide it is in total.
  //
  // The section headings are centred on this rather than on the table, so a
  // heading stays in the middle of what the reader can actually see at any
  // scroll position. There is no way to ask for that in CSS alone: a sticky
  // element's offsets resolve percentages against its containing block - here a
  // cell as wide as the whole roster - not against the pane it is stuck to. So
  // the pane's own width is measured and handed to the stylesheet.
  const paneRef = useRef(null);
  const [paneWidth, setPaneWidth] = useState(null);

  useEffect(() => {
    const pane = paneRef.current;
    if (!pane || typeof ResizeObserver === 'undefined') return undefined;

    // clientWidth rather than the observer's contentRect: it excludes the
    // vertical scrollbar, which is exactly the strip the headings must not be
    // centred over. The sheet is tall enough to have one.
    const measure = () => {
      setPaneWidth((current) => (current === pane.clientWidth ? current : pane.clientWidth));
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(pane);
    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    const table = tableRef.current;
    if (!table) return;

    // The journal block is excluded: its labels are QuickBooks account paths,
    // and sizing the column to fit one on a line would waste that width on
    // every other row. Those wrap instead.
    const spans = table.querySelectorAll(
      '.payroll-matrix__block:not(.payroll-matrix__block--journal) .payroll-matrix__label-text'
    );

    let widest = 0;
    spans.forEach((span) => { widest = Math.max(widest, span.scrollWidth); });
    if (widest === 0) return;

    // + the cell's 16px padding either side, + 1px for the column's own border.
    const next = Math.round(Math.min(340, Math.max(130, widest + 33)));
    setLabelColumnWidth((current) => (current === next ? current : next));
    // Re-measured when the run changes, since the category, plan and journal
    // labels - and the match percentage in one hint - all come from it.
  }, [result]);


  // The expenses behind a reimbursement total, as a hover summary. Capped: a
  // tooltip listing twenty expenses is a wall, and the dialog behind the cell
  // is where the full list belongs.
  const reimbursementTip = (employee) => {
    const items = employee.reimbursementItems || [];
    const shown = items.slice(0, 6);
    return (
      <div className="payroll-reimb__tip">
        {shown.map((item) => (
          <div className="payroll-reimb__tipRow" key={item.id}>
            <span className="payroll-reimb__tipName">
              {item.vendor || getCategoryLabel(item.category) || 'Expense'}
              {/* Which bucket the line is in. Without it a list of six
                  identical-looking rows adds up to a total none of them
                  explain - two are owed, one was paid last week, three are
                  still with an approver. */}
              <span className={`payroll-reimb__stage payroll-reimb__stage--${item.stage || 'paid'}`}>
                {REIMBURSEMENT_STAGE_LABEL[item.stage] || 'paid'}
              </span>
            </span>
            <span className="payroll-reimb__tipAmount bdc-num">{fmtMoney(item.amount)}</span>
          </div>
        ))}
        {items.length > shown.length ? (
          <div className="payroll-reimb__tipMore">+{items.length - shown.length} more</div>
        ) : null}
        <div className="payroll-reimb__tipTotal">
          <span>
            {employee.reimbursementCount} owed
            {employee.reimbursementUnpaidCount > 0
              ? ` (${employee.reimbursementUnpaidCount} unpaid)`
              : ''}
          </span>
          <span className="bdc-num">{fmtMoney(expenseReimbursementOf(employee))}</span>
        </div>
        {employee.reimbursementPendingCount > 0 ? (
          <div className="payroll-reimb__tipTotal payroll-reimb__tipTotal--pending">
            <span>{employee.reimbursementPendingCount} awaiting approval</span>
            <span className="bdc-num">{fmtMoney(employee.reimbursementPending)}</span>
          </div>
        ) : null}
        <div className="payroll-reimb__tipHint">Click for detail</div>
      </div>
    );
  };

  // A figure that belongs to the run rather than to any one employee: the label
  // cell, then one value spanning every employee column.
  //
  // The figure is wrapped so it can pin. The cell is as wide as the roster, so
  // on a wide run its number would scroll off to the left long before the cell
  // did, leaving an acre of empty row with the total nowhere on screen. The
  // span rides beside the label column instead - see the sticky rule in the
  // stylesheet.
  const runFigureRow = (label, value, emphasise = false) => (
    <tr key={label} className={emphasise ? 'payroll-matrix__total' : undefined}>
      {headerCell(label)}
      <td
        colSpan={employees.length}
        className={`bdc-num payroll-matrix__value payroll-matrix__runfigure${emphasise ? ' payroll-matrix__runfigure--total' : ''}`}
      >
        <span className="payroll-matrix__runfigure-value">
          {value == null ? EMPTY : (
            <PayrollCopyableFigure value={value} label={label}>
              {fmtMoney(value)}
            </PayrollCopyableFigure>
          )}
        </span>
      </td>
    </tr>
  );

  // A section heading inside the sheet. One cell across the whole table rather
  // than a label cell plus empties, so it reads as a rule with a title on it
  // instead of another row of data with a blank in every column.
  const sectionBand = (icon, label) => (
    <tr className="payroll-matrix__band">
      <td colSpan={employees.length + 1}>
        <span className="payroll-matrix__band-text">
          <i className={`bi ${icon}`} aria-hidden="true" />
          {label}
        </span>
      </td>
    </tr>
  );

  // `help` overrides the ROW_HELP lookup. The journal lines need it: two of
  // them share an account name, so the label alone cannot pick their tooltip.
  const headerCell = (label, help, badge = null) => {
    const cell = (
      <th className="payroll-matrix__label" scope="row">
        {/* The badge sits inside label-text so the column measurement in the
            layout effect above takes it into account - it is measured from that
            span, and a badge hung outside it would be a badge the column has no
            room for. */}
        <span className="payroll-matrix__label-text">
          {label}
          {badge}
        </span>
      </th>
    );

    const text = help || ROW_HELP[label];
    if (!text) return cell;

    // Whisper wraps the whole cell rather than the text, so the hit area is the
    // label column itself - the row is what the explanation is about. It opens
    // above: to the right it would cover the figures the reader is asking
    // about.
    return (
      <Whisper placement="top" trigger="hover" speaker={<Tooltip className="payroll-matrix__tip">{text}</Tooltip>} delayOpen={200}>
        {cell}
      </Whisper>
    );
  };

  if (employees.length === 0) return null;

  const { period } = result;

  // The three amounts added up. Summed from the parts rather than read from
  // employeeTotal so the row always agrees with the three above it, including
  // the employer match which employeeTotal does not carry.
  // What has to be paid out for this run: wages plus the expenses being paid
  // back. Summed from the parts that exist, so a run where no rate is on file
  // still reports the reimbursements rather than nothing at all.
  const cashParts = [
    result.totals?.totalCompensation,
    result.totals?.reimbursements,
  ].filter((v) => v != null);
  const cashNeeded = cashParts.length > 0
    ? Math.round((cashParts.reduce((a, b) => a + b, 0) + Number.EPSILON) * 100) / 100
    : null;

  const empowerParts = [
    result.totals?.retirement?.traditional,
    result.totals?.retirement?.roth,
    result.totals?.retirement?.employerMatch,
    result.totals?.retirement?.adjustment,
  ].filter((v) => v != null);
  const empowerTotal = empowerParts.length > 0
    ? Math.round((empowerParts.reduce((a, b) => a + b, 0) + Number.EPSILON) * 100) / 100
    : null;

  return (
    <div
      className="nsi-table-wrap payroll-matrix"
      ref={paneRef}
      style={paneWidth ? { '--payroll-view-w': `${paneWidth}px` } : undefined}
    >
      <div className="bdc-table-head">
        <div className="bdc-table-head__heading">
          <h3 className="bdc-table-head__title">Pay Period Summary</h3>
          <span className="bdc-count">{employees.length}</span>
          <span className="bdc-table-head__note">
            {period.workingDays} weekdays &middot; {fmtHours(period.standardPeriodHours)} h at a full schedule
          </span>
          {/* A filed run that was changed afterwards has to look changed. This
              is the whole audit trail for the reimbursement sync, and it is why
              the sync can be allowed at all. */}
          {result.reimbursementsRefreshedAt ? (
            <Whisper
              trigger="hover"
              placement="top"
              speaker={(
                <Tooltip>
                  Reimbursements on this run were synced with live expenses
                  {result.reimbursementsRefreshedBy ? ` by ${result.reimbursementsRefreshedBy}` : ''}.
                  Hours and pay are as originally filed.
                </Tooltip>
              )}
            >
              <span className="payroll-matrix__refreshed">
                <i className="bi bi-arrow-repeat" aria-hidden="true" />
                Reimbursements synced {fmtStamp(result.reimbursementsRefreshedAt)}
              </span>
            </Whisper>
          ) : null}
        </div>
        {onExport || onEditSettings ? (
          <div className="bdc-table-head__actions">
            {onExport ? (
              <button type="button" className="bdc-btn bdc-btn--sm" onClick={onExport}>
                <i className="bi bi-file-earmark-spreadsheet" aria-hidden="true" /> Export to Excel
              </button>
            ) : null}
            {onEditSettings ? (
              <button type="button" className="bdc-btn bdc-btn--sm" onClick={onEditSettings}>
                <i className="bi bi-sliders" aria-hidden="true" /> Payroll settings
              </button>
            ) : null}
          </div>
        ) : null}
      </div>

      <table
        ref={tableRef}
        className="nsi-table nsi-table--gridlines payroll-matrix__table"
        style={{
          minWidth: labelColumnWidth + employees.length * MIN_EMPLOYEE_COL,
          '--payroll-label-w': `${labelColumnWidth}px`,
        }}
      >
        {/*
          The label column is the only one with a width. Under table-layout:
          fixed, surplus width is split equally between the columns that have
          NO width and shared out in proportion when they all do - so giving the
          employee columns a width too was handing the label column a slice of
          every spare pixel, which is how 162px was rendering at well over 300.
          Left auto, they take the surplus and the label column keeps its size.
        */}
        <colgroup>
          <col style={{ width: labelColumnWidth }} />
          {employees.map((employee) => (
            <col key={employee.employeeId} />
          ))}
        </colgroup>

        <thead>
          <tr>
            <th className="payroll-matrix__corner" />
            {employees.map((employee) => (
              <th key={employee.employeeId} className="payroll-matrix__name" scope="col">
                {employee.name}
              </th>
            ))}
          </tr>
        </thead>

        {/* Who the person is and what they are committed to - the terms the
            hours are then measured against. */}
        <tbody className="payroll-matrix__block payroll-matrix__block--profile">
          <tr>
            {headerCell('TAWH')}
            {employees.map((employee) => (
              <td key={employee.employeeId} className="bdc-num payroll-matrix__value">
                {fmtHours(employee.tawhHours)}
                {/*
                  Always says what the figure above is measured against, full
                  commitment included: a blank line under a full-timer read as
                  missing data next to a colleague who had one, and "100%" is
                  worth stating rather than leaving to be inferred.

                  An hourly employee is told something different, because a
                  percentage is not what they are held to - they are forced to
                  100% a few lines up in resolveTawh, so repeating it here would
                  be reporting the default back as if it were a fact about them.
                */}
                <span className="payroll-matrix__sub">
                  {employee.nonExempt
                    ? (employee.targetHours == null
                        ? 'no target set'
                        : `${fmtHours(employee.targetHours)} h target`)
                    : `${employee.tawhPercent}% commitment`}
                </span>
              </td>
            ))}
          </tr>
          <tr>
            {headerCell('Classification')}
            {employees.map((employee) => (
              <td key={employee.employeeId} className="payroll-matrix__value">
                <span
                  className={`payroll-matrix__pill payroll-matrix__pill--${employee.classification === 'exempt' ? 'exempt' : 'nonexempt'}`}
                >
                  {employee.classificationLabel}
                </span>
              </td>
            ))}
          </tr>

          <tr>
            {headerCell('Rate')}
            {employees.map((employee) => (
              <td key={employee.employeeId} className="bdc-num payroll-matrix__value">
                {employee.payRate == null ? (
                  <span className="payroll-matrix__missing" title="No pay rate on file for this employee">
                    {EMPTY}
                  </span>
                ) : (
                  <>
                    {fmtMoney(employee.payRate)}
                    {employee.rateChangedInPeriod ? (
                      <span
                        className="payroll-matrix__sub payroll-matrix__sub--warn"
                        title={`A new rate took effect on ${employee.rateEffectiveDate}, inside this pay period. The rate shown is the one in force at the end of it.`}
                      >
                        <i className="bi bi-exclamation-triangle" aria-hidden="true" /> changed {employee.rateEffectiveDate}
                      </span>
                    ) : null}
                  </>
                )}
              </td>
            ))}
          </tr>
        </tbody>

        {/* The hours themselves. Set apart from the block above because these
            are what was pulled, not what was agreed. */}
        <tbody className="payroll-matrix__block payroll-matrix__block--hours">
          {sectionBand('bi-clock-history', 'Raw Hours')}

          {(result.categories || []).map((category) => (
            <tr key={category.key}>
              {headerCell(category.label)}
              {employees.map((employee) => {
                const hours = employee.categories.find((c) => c.key === category.key)?.hours ?? 0;
                return (
                  <td key={employee.employeeId} className="bdc-num payroll-matrix__value">
                    {fmtHours(hours)}
                  </td>
                );
              })}
            </tr>
          ))}

          <tr className="payroll-matrix__subtotal">
            {headerCell('Total Hours')}
            {employees.map((employee) => (
              <td key={employee.employeeId} className="bdc-num payroll-matrix__value">
                <PayrollCopyableFigure value={employee.totalHours} label={`${employee.name} total hours`}>
                  {fmtHours(employee.totalHours)}
                </PayrollCopyableFigure>
              </td>
            ))}
          </tr>

          <tr>
            {headerCell('Payroll Variance')}
            {employees.map((employee) => {
              if (!hasVariance(employee)) {
                return (
                  <td key={employee.employeeId} className="bdc-num payroll-matrix__value">
                    <span className="payroll-matrix__missing">{EMPTY}</span>
                  </td>
                );
              }
              // The liability leads and the hours it came from explain it.
              // Nothing about what was paid belongs here - that is the Paid
              // Hours row's job, and mixing the two in one cell is what would
              // make this figure ambiguous.
              return (
                <td
                  key={employee.employeeId}
                  className="bdc-num payroll-matrix__value payroll-matrix__value--low"
                >
                  {employee.varianceAmount == null ? EMPTY : fmtMoney(employee.varianceAmount)}
                  <span className="payroll-matrix__sub">{fmtHours(employee.varianceHours)} h</span>
                </td>
              );
            })}
          </tr>


          <tr>
            {headerCell('Billable %')}
            {employees.map((employee) => (
              <td
                key={employee.employeeId}
                className={`bdc-num payroll-matrix__value ${billableTone(employee.billablePercent)}`}
              >
                {employee.billablePercent == null ? EMPTY : fmtPercent(employee.billablePercent)}
              </td>
            ))}
          </tr>

          <tr>
            {headerCell('Paid Hours')}
            {employees.map((employee) => (
              <td key={employee.employeeId} className="bdc-num payroll-matrix__value">
                {fmtHours(employee.compensatedHours)}
              </td>
            ))}
          </tr>
        </tbody>

        {/* The PATO bank. An annual allowance, so what is left is measured
            against everything taken since January, not against this fortnight. */}
        <tbody className="payroll-matrix__block payroll-matrix__block--benefits">
          {sectionBand('bi-gift', 'Benefits')}

          <tr>
            {headerCell('PATO Used')}
            {employees.map((employee) => (
              <td key={employee.employeeId} className="bdc-num payroll-matrix__value">
                {fmtHours(employee.patoUsedPeriod)}
              </td>
            ))}
          </tr>

          <tr>
            {headerCell('PATO Remaining')}
            {employees.map((employee) => (
              <td
                key={employee.employeeId}
                className={`bdc-num payroll-matrix__value ${employee.patoRemaining < 0 ? 'payroll-matrix__value--low' : ''}`}
              >
                {/* Unlimited time off has no bank, so nothing remains of
                    anything; a run saved before the ledger has no flag and
                    always has a figure. */}
                {employee.patoUnlimited ? (
                  'Unlimited'
                ) : (
                  <>
                    {fmtHours(employee.patoRemaining)}
                    <span className="payroll-matrix__sub">
                      of {fmtHours(employee.patoAllowance)} h
                      {employee.patoRemaining < 0 ? ' \u00b7 overdrawn' : ''}
                    </span>
                  </>
                )}
              </td>
            ))}
          </tr>
        </tbody>

        {/* What the hours come to, and what is paid back on top of them. */}
        <tbody className="payroll-matrix__block payroll-matrix__block--pay">
          {sectionBand('bi-cash-coin', 'Compensation')}

          <tr className="payroll-matrix__correction-row">
            {headerCell('Payroll Correction')}
            {employees.map((employee) => (
              <td key={employee.employeeId} className="payroll-matrix__value">
                {onSaveCorrection ? (
                  <PayrollCorrectionCell
                    employee={employee}
                    onClear={clearCorrection}
                    onRequestReason={requestReason}
                    onSaveKeepingNote={saveKeepingNote}
                    resetToken={resetToken}
                  />
                ) : (
                  <span className="bdc-num">
                    {employee.correction ? fmtMoney(employee.correction) : EMPTY}
                  </span>
                )}
              </td>
            ))}
          </tr>

          {/* B26 / B27 in the sheet. Sit between the correction and the total
              because that is the order they feed it in: hours over the
              commitment, what those hours are worth, then the whole figure. */}
          <tr>
            {headerCell('Total Added Direct Hours')}
            {employees.map((employee) => (
              <td key={employee.employeeId} className="bdc-num payroll-matrix__value">
                {fmtHours(employee.addedDirectHours)}
              </td>
            ))}
          </tr>

          <tr>
            {headerCell('Added Direct Hours Compensation')}
            {employees.map((employee) => (
              <td key={employee.employeeId} className="bdc-num payroll-matrix__value">
                {employee.addedDirectCompensation == null
                  ? EMPTY
                  : fmtMoney(employee.addedDirectCompensation)}
              </td>
            ))}
          </tr>

          <tr className="payroll-matrix__total">
            {headerCell('Total Compensation')}
            {employees.map((employee) => (
              <td key={employee.employeeId} className="bdc-num payroll-matrix__value">
                {employee.totalCompensation == null ? EMPTY : (
                  <PayrollCopyableFigure
                    value={employee.totalCompensation}
                    label={`${employee.name} total compensation`}
                  >
                    {fmtMoney(employee.totalCompensation)}
                  </PayrollCopyableFigure>
                )}
              </td>
            ))}
          </tr>

          <tr>
            {headerCell(
              'Reimbursements',
              // The drift note is appended to the row's own help rather than
              // given a tooltip of its own. The badge sits inside the label
              // cell, and that cell is already the hover target for the row
              // explanation - a second Whisper nested in the first put two
              // tooltips on screen at once.
              reimbursementDrift?.changed
                ? `${ROW_HELP.Reimbursements} Expenses have changed since this run was filed - click the warning to review and sync.`
                : null,
              // Only on a saved run whose expenses have moved on. A filed run is
              // frozen, which is right for hours and wrong for expenses that get
              // approved while payroll is being done - so rather than silently
              // updating, the sheet says something changed and leaves the
              // decision here.
              reimbursementDrift?.changed ? (
                <button
                  type="button"
                  className="payroll-matrix__driftbadge"
                  onClick={onOpenReimbursementDrift}
                  aria-label="Expenses have changed since this run was filed. Review and sync."
                >
                  <i className="bi bi-exclamation-triangle-fill" aria-hidden="true" />
                  {reimbursementDrift.employees?.length || 0}
                </button>
              ) : null
            )}
            {employees.map((employee) => {
              const items = employee.reimbursementItems || [];
              // The sub-line says what the figure is made of rather than just
              // how many rows it has: an unpaid backlog and a clean period both
              // showed "3 expenses" before, and only one of them needs acting
              // on. Pending sits under it in its own tone because it is not
              // part of the figure at all.
              const parts = [];
              if (employee.reimbursementPaidCount > 0) {
                parts.push(`${employee.reimbursementPaidCount} paid`);
              }
              if (employee.reimbursementUnpaidCount > 0) {
                parts.push(`${employee.reimbursementUnpaidCount} unpaid`);
              }

              const figure = (
                <>
                  {fmtMoney(expenseReimbursementOf(employee))}
                  {parts.length > 0 ? (
                    <span className="payroll-matrix__sub">{parts.join(' · ')}</span>
                  ) : null}
                  {employee.reimbursementPendingCount > 0 ? (
                    <span className="payroll-matrix__sub payroll-matrix__sub--warn">
                      <i className="bi bi-hourglass-split" aria-hidden="true" />{' '}
                      {fmtMoney(employee.reimbursementPending)} awaiting approval
                    </span>
                  ) : null}
                </>
              );

              // Nothing to break down means nothing to hover or open - a
              // zero stays an ordinary cell. `items` covers pending too, so
              // somebody owed nothing but with a claim in the queue still gets
              // the hover that says so.
              if (items.length === 0) {
                return (
                  <td key={employee.employeeId} className="bdc-num payroll-matrix__value">
                    {figure}
                  </td>
                );
              }

              return (
                <Whisper
                  key={employee.employeeId}
                  placement="top"
                  trigger="hover"
                  delayOpen={200}
                  speaker={<Tooltip className="payroll-matrix__tip">{reimbursementTip(employee)}</Tooltip>}
                >
                  <td
                    className="bdc-num payroll-matrix__value payroll-reimb__cell"
                    role="button"
                    tabIndex={0}
                    onClick={() => setReimbursementsFor(employee)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setReimbursementsFor(employee);
                      }
                    }}
                  >
                    {figure}
                  </td>
                </Whisper>
              );
            })}
          </tr>

          {/* Money owed that did not come through an expense - lunch for a
              meeting, say. Typed in like a payroll correction, reason and all,
              and added to the reimbursements total below. */}
          <tr className="payroll-matrix__correction-row">
            {headerCell(
              'Reimbursement Adjustment',
              undefined,
              // One amount for several people at once - the usual case for this
              // row. No tooltip of its own: the label cell already carries the
              // row's explanation, which says what this button does, and a
              // second Whisper nested in it would put two on screen.
              onSaveReimbursementAdjustmentBulk ? (
                <button
                  type="button"
                  className="payroll-matrix__groupbtn"
                  onClick={() => setBulkAdjustmentOpen(true)}
                  aria-label="Apply a reimbursement adjustment to several employees"
                >
                  <i className="bi bi-plus-lg" aria-hidden="true" />
                  <i className="bi bi-people-fill" aria-hidden="true" />
                </button>
              ) : null
            )}
            {employees.map((employee) => (
              <td key={employee.employeeId} className="payroll-matrix__value">
                {onSaveReimbursementAdjustment ? (
                  <PayrollCorrectionCell
                    employee={employee}
                    valueKey="reimbursementAdjustment"
                    noteKey="reimbursementAdjustmentNote"
                    prefix="$"
                    postfix={null}
                    onClear={(target) => clearCorrection(target, 'reimbursement')}
                    onRequestReason={(target, amount) => requestReason(target, amount, 'reimbursement')}
                    onSaveKeepingNote={(target, amount) => saveKeepingNote(target, amount, 'reimbursement')}
                    resetToken={resetToken}
                  />
                ) : employee.reimbursementAdjustment ? (
                  <Whisper
                    placement="top"
                    trigger="hover"
                    speaker={<Tooltip>{employee.reimbursementAdjustmentNote || 'No reason recorded.'}</Tooltip>}
                  >
                    <span className="bdc-num">{fmtMoney(employee.reimbursementAdjustment)}</span>
                  </Whisper>
                ) : (
                  <span className="bdc-num">{EMPTY}</span>
                )}
              </td>
            ))}
          </tr>

          {/* What the run costs, and the two halves of it. Run-level figures,
              so each is shown once rather than as a column per person. */}
          {runFigureRow('Compensation total', result.totals?.totalCompensation)}
          {runFigureRow('Reimbursements total', result.totals?.reimbursements)}
          {runFigureRow('Cash Needed', cashNeeded, true)}
        </tbody>

        {/* Deferrals and the match they earn. Its own block because this is
            money coming out of the compensation above, not part of it. */}
        <tbody className="payroll-matrix__block payroll-matrix__block--retirement">
          {sectionBand('bi-piggy-bank', 'Retirement')}

          {(result.retirementPlans || []).map((plan) => (
            <tr key={plan.key}>
              {headerCell(plan.label)}
              {employees.map((employee) => {
                const contribution = employee.retirement?.plans?.[plan.key];
                return (
                  <td key={employee.employeeId} className="bdc-num payroll-matrix__value">
                    {contribution == null ? (
                      <span className="payroll-matrix__missing">{EMPTY}</span>
                    ) : (
                      <>
                        {contribution.amount == null ? EMPTY : fmtMoney(contribution.amount)}
                        {/* The election behind the figure, so a number can be
                            checked against what was actually entered. */}
                        <span className="payroll-matrix__sub">{describeElection(contribution)}</span>
                      </>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}

          <tr className="payroll-matrix__total">
            {headerCell('Employer Match')}
            {employees.map((employee) => {
              const match = employee.retirement?.match;
              return (
                <td key={employee.employeeId} className="bdc-num payroll-matrix__value">
                  {match?.amount == null ? EMPTY : (
                    <PayrollCopyableFigure
                      value={match.amount}
                      label={`${employee.name} employer match`}
                    >
                      {fmtMoney(match.amount)}
                    </PayrollCopyableFigure>
                  )}
                  {match?.appliedPercent != null ? (
                    <span className="payroll-matrix__sub">{fmtPercent(match.appliedPercent)} applied</span>
                  ) : null}
                </td>
              );
            })}
          </tr>

          <tr>
            {headerCell('Loan Repayment')}
            {employees.map((employee) => (
              <td key={employee.employeeId} className="payroll-matrix__value">
                {onSaveAdjustment ? (
                  <PayrollAdjustmentCell
                    employee={employee}
                    onSave={onSaveAdjustment}
                    resetToken={resetToken}
                  />
                ) : (
                  <span className="bdc-num">{fmtMoney(employee.retirement?.adjustment || 0)}</span>
                )}
              </td>
            ))}
          </tr>
        </tbody>

        {/* What gets posted to QuickBooks. Its own named section because the
            labels are QBO account names, matched by hand over there, rather
            than descriptions of a calculation. */}
        <tbody className="payroll-matrix__block payroll-matrix__block--journal">
          {sectionBand('bi-journal-text', 'Journal Entry')}

          {/* The names again. The journal is keyed into QuickBooks a person at
              a time, and on a long sheet the pinned header is a long way from
              the figures being read off. */}
          <tr className="payroll-matrix__namerow">
            <th className="payroll-matrix__namerow-corner" />
            {employees.map((employee) => (
              <th key={employee.employeeId} className="payroll-matrix__namerow-name" scope="col">
                {employee.name}
              </th>
            ))}
          </tr>

          {(result.journalLines || []).map((line) => (
            <tr key={line.key}>
              {headerCell(line.label, line.help)}
              {employees.map((employee) => {
                const value = employee.journal?.[line.key];
                return (
                  <td key={employee.employeeId} className="bdc-num payroll-matrix__value">
                    {value == null
                      ? <span className="payroll-matrix__missing">{EMPTY}</span>
                      : (line.unit === 'money' ? fmtMoney(value) : fmtHours(value))}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>

        {/* What goes to Empower, broken into the amounts that make it up and
            then their sum. Run-level figures, not a column per person - this
            is what gets uploaded, so each is shown once. */}
        <tbody className="payroll-matrix__block payroll-matrix__block--checksums">
          {sectionBand('bi-check2-square', 'Checksums')}

          {runFigureRow('Employee 401(k) total', result.totals?.retirement?.traditional)}
          {runFigureRow('Employee Roth total', result.totals?.retirement?.roth)}
          {runFigureRow('Employer match total', result.totals?.retirement?.employerMatch)}
          {runFigureRow('Loan Repayment total', result.totals?.retirement?.adjustment)}
          {runFigureRow('Total uploaded to Empower', empowerTotal, true)}
        </tbody>
      </table>

      <PayrollReimbursementsModal
        open={!!reimbursementsFor}
        employee={reimbursementsFor}
        period={period}
        onClose={() => setReimbursementsFor(null)}
        drift={reimbursementDrift}
        onSync={onSyncReimbursements}
        syncing={syncingReimbursements}
      />

      <PayrollCorrectionModal
        open={!!correctionTarget}
        target={correctionTarget ? { ...correctionTarget, period: `${period.start} to ${period.end}` } : null}
        onClose={dismissCorrection}
        onSave={saveCorrection}
        saving={savingCorrection}
      />

      <PayrollBulkReimbursementAdjustmentModal
        open={bulkAdjustmentOpen}
        employees={employees}
        onClose={() => setBulkAdjustmentOpen(false)}
        onApply={applyBulkAdjustment}
        saving={savingBulkAdjustment}
      />
    </div>
  );
}
